import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../hooks.ts';
import { getNorms } from '../../store/selectors/user.selector.ts';
import { MarketMatchResultsPreviewModal } from './sidebar/MarketMatchResultsPreviewModal.tsx';
import {
  formatLumen,
  getLumenDeltaColor,
  useAppliedMarketMatch,
  useAppliedMatchSummary,
} from './useAppliedMarketMatch.ts';

/**
 * Floating popup in the workspace upper-right corner showing which
 * market-match configuration is currently applied, with a total lumen
 * output comparison vs. the original (optimal) configuration.
 *
 * Hidden when no selection is applied or when the matching candidate /
 * similarity detail is unavailable (callers are expected to have loaded
 * both before the user can apply a selection).
 *
 * Workspace layout v2 shows the applied match in the side panel's footer instead.
 */
export function AppliedMarketMatchBadge() {
  const { t } = useTranslation();
  const summary = useAppliedMatchSummary();
  const { unapply, createVersion, isCreatingVersion, canCreateVersion } = useAppliedMarketMatch();
  const norms = useAppSelector(getNorms);
  const [isResultsPreviewOpen, setIsResultsPreviewOpen] = useState(false);

  if (!summary) return null;
  const { detail, productName, placementCount, catalogueUrl, targets } = summary;

  return (
    <>
      <div className="absolute top-4 right-4 z-30 w-[340px]">
        <div className="flex flex-col gap-3 bg-[#1E1E1E] border border-white/10 rounded-xl shadow-2xl p-3">
          <Header
            label={t('workspace.appliedConfiguration')}
            productName={productName}
            manufacturerName={detail.manufacturerName}
            imageUrl={detail.imageUrl}
            placementCount={placementCount}
            placementLabel={placementCount > 0 ? t('workspace.placements', { count: placementCount }) : null}
            catalogueUrl={catalogueUrl}
            catalogueLabel={t('workspace.viewInCatalogue')}
          />

          {targets.total > 0 && (
            <TargetsPill
              satisfied={targets.satisfied}
              total={targets.total}
              allSatisfied={summary.allTargetsSatisfied}
              label={t('workspace.targetsSatisfied', targets)}
              onClick={() => setIsResultsPreviewOpen(true)}
            />
          )}

          <LumenComparison
            appliedTotalLumen={summary.appliedTotalLumen}
            idealTotalLumen={summary.idealTotalLumen}
            lumenDeltaPct={summary.lumenDeltaPct}
            appliedLabel={t('workspace.appliedConfigurationTotalLumen')}
            idealLabel={t('workspace.appliedConfigurationIdealLumen')}
            naText={t('common.n_a')}
          />

          <Actions
            unapplyLabel={t('workspace.unapply')}
            newVersionLabel={t('workspace.createVersionWithConfiguration')}
            isCreatingVersion={isCreatingVersion}
            showNewVersion={canCreateVersion}
            onUnapply={unapply}
            onNewVersion={createVersion}
          />
        </div>
      </div>

      <MarketMatchResultsPreviewModal
        isOpen={isResultsPreviewOpen}
        lampName={productName}
        aggregateResults={summary.aggregateResults}
        layerDisplayNames={summary.layerDisplayNames}
        norms={norms}
        onClose={() => setIsResultsPreviewOpen(false)}
      />
    </>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────────

type HeaderProps = {
  label: string;
  productName: string;
  manufacturerName?: string;
  imageUrl?: string;
  placementCount: number;
  placementLabel: string | null;
  catalogueUrl: string | null;
  catalogueLabel: string;
};

function Header({
  label,
  productName,
  manufacturerName,
  imageUrl,
  placementLabel,
  catalogueUrl,
  catalogueLabel,
}: HeaderProps) {
  return (
    <div className="flex items-start gap-2.5">
      {imageUrl && (
        <div className="w-11 h-11 rounded-md overflow-hidden flex-shrink-0 bg-stone-900 border border-stone-700 flex items-center justify-center p-0.5">
          <img src={imageUrl} alt={productName} className="max-w-full max-h-full object-contain" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[9px] text-lime-400 uppercase tracking-wider font-semibold leading-tight truncate">
          {label}
        </p>
        {manufacturerName && (
          <p className="text-[10px] text-gray-400 truncate leading-tight" title={manufacturerName}>
            {manufacturerName}
          </p>
        )}
        <p className="text-sm text-white font-semibold truncate leading-tight mt-0.5" title={productName}>
          {productName}
        </p>
        {(placementLabel || catalogueUrl) && (
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            {placementLabel && (
              <span className="text-[10px] text-gray-500 whitespace-nowrap flex-shrink-0">{placementLabel}</span>
            )}
            {catalogueUrl && (
              <a
                href={catalogueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-lime-400 hover:underline leading-tight truncate min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                {catalogueLabel}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type TargetsPillProps = {
  satisfied: number;
  total: number;
  allSatisfied: boolean;
  label: string;
  onClick: () => void;
};

function TargetsPill({ allSatisfied, label, onClick }: TargetsPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`self-start text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 transition-colors cursor-pointer ${
        allSatisfied
          ? 'text-lime-500/90 bg-lime-500/10 hover:bg-lime-500/20'
          : 'text-red-500/90 bg-red-500/10 hover:bg-red-500/20'
      }`}
    >
      <div className={`w-1.5 h-1.5 rounded-full ${allSatisfied ? 'bg-lime-500' : 'bg-red-500'}`} />
      {label}
    </button>
  );
}

type LumenComparisonProps = {
  appliedTotalLumen: number | null;
  idealTotalLumen: number | null;
  lumenDeltaPct: number | null;
  appliedLabel: string;
  idealLabel: string;
  naText: string;
};

function LumenComparison({
  appliedTotalLumen,
  idealTotalLumen,
  lumenDeltaPct,
  appliedLabel,
  idealLabel,
  naText,
}: LumenComparisonProps) {
  if (appliedTotalLumen == null && idealTotalLumen == null) return null;

  return (
    <div className="grid grid-cols-2 gap-2 border-t border-white/5 pt-2">
      <LumenStat label={appliedLabel} value={appliedTotalLumen} delta={lumenDeltaPct} naText={naText} emphasized />
      <LumenStat label={idealLabel} value={idealTotalLumen} naText={naText} />
    </div>
  );
}

type LumenStatProps = {
  label: string;
  value: number | null;
  naText: string;
  emphasized?: boolean;
  delta?: number | null;
};

function LumenStat({ label, value, naText, emphasized, delta }: LumenStatProps) {
  const formatted = value != null ? formatLumen(value) : naText;
  return (
    <div className="min-w-0">
      <p className="text-[9px] text-gray-500 uppercase tracking-wider leading-tight truncate">{label}</p>
      <p
        className={`text-xs font-semibold leading-tight mt-0.5 truncate ${emphasized ? 'text-white' : 'text-gray-300'}`}
        title={value != null ? formatted : undefined}
      >
        {formatted}
      </p>
      {delta != null && (
        <p className={`text-[10px] font-medium leading-tight mt-0.5 truncate ${getLumenDeltaColor(delta)}`}>
          {delta > 0 ? '+' : ''}
          {delta.toFixed(1)}%
        </p>
      )}
    </div>
  );
}

type ActionsProps = {
  unapplyLabel: string;
  newVersionLabel: string;
  isCreatingVersion: boolean;
  showNewVersion: boolean;
  onUnapply: () => void;
  onNewVersion: () => void;
};

function Actions({
  unapplyLabel,
  newVersionLabel,
  isCreatingVersion,
  showNewVersion,
  onUnapply,
  onNewVersion,
}: ActionsProps) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <button
        type="button"
        onClick={onUnapply}
        disabled={isCreatingVersion}
        className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded bg-stone-700 text-white hover:bg-stone-600 transition-colors disabled:opacity-50 disabled:cursor-wait"
      >
        {unapplyLabel}
      </button>
      {showNewVersion && (
        <button
          type="button"
          onClick={onNewVersion}
          disabled={isCreatingVersion}
          className="flex-1 min-w-0 text-xs font-semibold px-3 py-1.5 rounded bg-lime-500 text-black hover:bg-lime-400 transition-colors disabled:opacity-50 disabled:cursor-wait truncate"
        >
          {newVersionLabel}
        </button>
      )}
    </div>
  );
}
